/**
 * Agent monitoring panel for the incident log page: start button when no
 * agent has the incident, otherwise the session header (status, spend,
 * next wake), the live activity timeline, and a message composer so a
 * human can talk to the agent mid-incident.
 */
import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FlagIcon from '@mui/icons-material/Flag';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SummarizeIcon from '@mui/icons-material/Summarize';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
    AgentLogEntry,
    AgentLogEntryType,
    AgentSessionSummary,
    useIncidentAgentSession,
    useSendAgentMessage,
    useStartAgentSession,
    useStopAgentSession,
} from '../api/agentSessions';

const TEAL = '#00d4ad';

function formatWhen(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const ENTRY_ICONS: Record<AgentLogEntryType, React.ReactNode> = {
    wake: <ScheduleIcon fontSize="small" sx={{ color: 'text.disabled' }} />,
    observation: <VisibilityIcon fontSize="small" sx={{ color: '#0ea5e9' }} />,
    consideration: <PsychologyIcon fontSize="small" sx={{ color: '#8b5cf6' }} />,
    decision: <FactCheckIcon fontSize="small" sx={{ color: TEAL }} />,
    action: <BuildIcon fontSize="small" sx={{ color: 'text.disabled' }} />,
    notification: (
        <NotificationsActiveIcon fontSize="small" sx={{ color: '#f59e0b' }} />
    ),
    message: <ChatBubbleOutlineIcon fontSize="small" sx={{ color: '#0ea5e9' }} />,
    status: <FlagIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
    summary: <SummarizeIcon fontSize="small" sx={{ color: TEAL }} />,
    error: <ErrorOutlineIcon fontSize="small" sx={{ color: '#ef4444' }} />,
    thinking: <LightbulbOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />,
};

/** The agent's reasoning before a step: on the record, but folded away so
 *  the timeline reads as what it saw, decided and did. */
function ThinkingEntry({ entry }: { entry: AgentLogEntry }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ display: 'flex', gap: 1.25, mb: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{ mt: 0.25 }}>{ENTRY_ICONS.thinking}</Box>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpen((v) => !v);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { opacity: 0.85 },
                    }}
                >
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {entry.title || 'Reasoning'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatWhen(entry.created_at)}
                    </Typography>
                    <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                            color: 'text.disabled',
                            transition: 'transform 150ms',
                            transform: open ? 'rotate(180deg)' : 'none',
                        }}
                    />
                </Box>
                <Collapse in={open} unmountOnExit>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            mt: 0.5,
                            pl: 1.25,
                            borderLeft: '2px solid',
                            borderColor: 'divider',
                            fontSize: '0.8rem',
                        }}
                    >
                        {entry.body}
                    </Typography>
                </Collapse>
            </Box>
        </Box>
    );
}

const STATUS_CHIP: Record<
    AgentSessionSummary['status'],
    { label: string; color: 'success' | 'default' | 'warning' | 'error' }
> = {
    active: { label: 'Active', color: 'success' },
    closed: { label: 'Closed by agent', color: 'default' },
    stopped: { label: 'Stopped', color: 'warning' },
    failed: { label: 'Failed', color: 'error' },
};

function TimelineEntry({ entry }: { entry: AgentLogEntry }) {
    if (entry.entry_type === 'thinking') return <ThinkingEntry entry={entry} />;
    const isAction = entry.entry_type === 'action';
    const actionFailed = isAction && entry.data?.is_error === true;
    return (
        <Box sx={{ display: 'flex', gap: 1.25, mb: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{ mt: 0.25 }}>{ENTRY_ICONS[entry.entry_type]}</Box>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                    <Typography
                        variant="body2"
                        fontWeight={isAction ? 400 : 600}
                        sx={isAction ? { fontFamily: 'monospace', fontSize: '0.8rem' } : {}}
                    >
                        {entry.title}
                        {actionFailed ? ' — failed' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatWhen(entry.created_at)}
                    </Typography>
                </Box>
                {entry.body && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}
                    >
                        {entry.body}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

/** Marks where a later session took over the fire: a reopened incident
 *  (re-detection inside a closed fire's footprint) keeps its log and starts
 *  a fresh session beneath it. */
function SessionDivider({ at }: { at: string }) {
    return (
        <Divider sx={{ my: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
                Incident reopened · new monitoring session {formatWhen(at)}
            </Typography>
        </Divider>
    );
}

export default function AgentSessionPanel({
    projectId,
    incidentId,
}: {
    projectId: string | undefined;
    incidentId: string | undefined;
}) {
    const { data, isLoading } = useIncidentAgentSession(projectId, incidentId);
    const start = useStartAgentSession(projectId, incidentId);
    const detail = data?.data;
    const session = detail?.session ?? null;
    const sendMessage = useSendAgentMessage(projectId, incidentId, session?.id);
    const stop = useStopAgentSession(projectId, incidentId, session?.id);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Feature not entitled/configured and no history: render nothing — the
    // page collapses to single-column (it keys off the same query), and
    // non-entitled tenants never see agent affordances at all.
    if (isLoading || !detail) {
        return <Paper variant="outlined" sx={{ p: 2.5, minHeight: 120 }} />;
    }
    if (!session && !detail.enabled) return null;

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2.5,
                borderColor: 'rgba(0, 212, 173, 0.4)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: { lg: '100%' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <SmartToyIcon sx={{ color: TEAL }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    Agent monitoring
                </Typography>
                {session && (
                    <>
                        {session.running && <CircularProgress size={14} sx={{ color: TEAL }} />}
                        <Chip
                            size="small"
                            color={STATUS_CHIP[session.status].color}
                            label={STATUS_CHIP[session.status].label}
                        />
                        {session.status === 'active' && (
                            <Button
                                size="small"
                                color="warning"
                                disabled={stop.isPending}
                                onClick={() => stop.mutate()}
                            >
                                Stop
                            </Button>
                        )}
                    </>
                )}
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            )}

            {!session ? (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Hand this incident to an AI agent. It monitors detections and
                        weather, runs fire simulations when conditions warrant, notifies
                        subscribers, and prepares operational briefs — recording every
                        observation, decision and action in this log.
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<SmartToyIcon />}
                        disabled={start.isPending}
                        onClick={() =>
                            start.mutate(undefined, {
                                onError: (e) =>
                                    setError(e?.message || 'Failed to start the agent'),
                            })
                        }
                        sx={{ color: TEAL, borderColor: 'rgba(0, 212, 173, 0.5)' }}
                    >
                        {start.isPending ? 'Starting…' : 'Monitor with AI'}
                    </Button>
                </>
            ) : (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        {session.wake_count} wake{session.wake_count === 1 ? '' : 's'} ·
                        spend ${session.spend_usd.toFixed(2)} of $
                        {session.spend_cap_usd.toFixed(0)} cap
                        {session.status === 'active' && session.next_wake_at
                            ? ` · next wake ${formatWhen(session.next_wake_at)}`
                            : ''}
                        {session.close_reason ? ` · ${session.close_reason}` : ''}
                    </Typography>

                    <Box
                        sx={{
                            flexGrow: 1,
                            minHeight: 120,
                            maxHeight: { xs: 480, lg: 'none' },
                            overflowY: 'auto',
                            pr: 1,
                        }}
                    >
                        {detail.entries.map((entry, i) => {
                            const prev = i > 0 ? detail.entries[i - 1] : null;
                            const newSession =
                                prev !== null && prev.session_id !== entry.session_id;
                            return (
                                <Box key={entry.id}>
                                    {newSession && <SessionDivider at={entry.created_at} />}
                                    <TimelineEntry entry={entry} />
                                </Box>
                            );
                        })}
                        {detail.entries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Waiting for the agent's first wake…
                            </Typography>
                        )}
                    </Box>

                    {session.status !== 'active' && detail.enabled && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SmartToyIcon />}
                            disabled={start.isPending}
                            onClick={() =>
                                start.mutate(undefined, {
                                    onError: (e) =>
                                        setError(e?.message || 'Failed to start the agent'),
                                })
                            }
                            sx={{ mt: 1.5, color: TEAL, borderColor: 'rgba(0, 212, 173, 0.5)' }}
                        >
                            {start.isPending ? 'Starting…' : 'Monitor with AI again'}
                        </Button>
                    )}

                    {session.status === 'active' && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Message the agent (e.g. 'hold updates until 07:00', 'rerun the simulation with stronger winds')"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey && draft.trim()) {
                                        e.preventDefault();
                                        sendMessage.mutate(draft.trim(), {
                                            onSuccess: () => setDraft(''),
                                            onError: (err) =>
                                                setError(err?.message || 'Failed to send'),
                                        });
                                    }
                                }}
                            />
                            <Tooltip title="The agent reads this on its next wake (immediately)">
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!draft.trim() || sendMessage.isPending}
                                        onClick={() =>
                                            sendMessage.mutate(draft.trim(), {
                                                onSuccess: () => setDraft(''),
                                                onError: (err) =>
                                                    setError(err?.message || 'Failed to send'),
                                            })
                                        }
                                        sx={{ height: '100%' }}
                                    >
                                        Send
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );
}
